import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import {
  Waveform,
  ShieldCheck,
  Sparkle,
  CurrencyEur,
  ChatCircleDots,
  Broadcast,
  Microphone,
  Robot,
} from "@phosphor-icons/react";

const features = [
  { icon: Broadcast, title: "Live Dolby HD", desc: "Diffusion immersive jusqu'à des millions de fans. Sortie son IA optimisée." },
  { icon: Microphone, title: "Contrôles studio", desc: "Audio/vidéo, effets sonores IA et kick instantané d'un spectateur." },
  { icon: ChatCircleDots, title: "Chat & emojis", desc: "Bulles flottantes, modération, ambiance live garantie." },
  { icon: CurrencyEur, title: "Billetterie flex", desc: "Concert gratuit ou payant — vous fixez le tarif et encaissez en toute simplicité." },
  { icon: Robot, title: "IA support 24/7", desc: "Echo détecte les bugs, conseille, et fait grandir votre fanbase." },
  { icon: ShieldCheck, title: "Coffre fanbase", desc: "Emails captés en toute sécurité pour annoncer vos futurs concerts." },
];

const artistNames = ["NOVA LUMIÈRE", "AURORA WAVE", "LUNA ÉLECTRIQUE", "SOLAR RIFT", "MIDNIGHT KAIRO", "VELVET STORM", "ECHO PRIME", "NEON ORCHID"];

const Landing = () => {
  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-20 pb-32 px-6">
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.pexels.com/photos/30215324/pexels-photo-30215324.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/60 to-[#050505]" />
        </div>

        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-8 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="md:col-span-8"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5 text-xs uppercase tracking-[0.2em] mb-6">
              <Sparkle size={14} weight="fill" className="text-[#F59E0B]" />
              <span>La révolution du concert virtuel</span>
            </div>

            <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-7xl leading-[0.95]">
              Votre scène, <br/>
              <span className="text-[#E11D48]">des millions</span><br/>
              de spectateurs.
            </h1>

            <p className="mt-6 text-zinc-300 text-lg max-w-xl leading-relaxed">
              XooKmédia est la plateforme tout-en-un où les artistes diffusent en live Dolby HD,
              fixent leur prix et construisent leur fanbase grâce à une IA dédiée.
              Une scène mondiale, sans intermédiaire.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <Button data-testid="hero-signup-artist" size="lg" className="bg-[#E11D48] hover:bg-[#BE123C] text-white px-7" asChild>
                <Link to="/signup?role=artist">Devenir artiste — Gratuit</Link>
              </Button>
              <Button data-testid="hero-explore" variant="outline" size="lg" className="border-white/20 text-white hover:bg-white/10 hover:text-white px-7" asChild>
                <Link to="/concerts">Explorer les concerts</Link>
              </Button>
              <Button data-testid="hero-install" variant="ghost" size="lg" className="text-zinc-300 hover:text-white hover:bg-white/5 px-5" asChild>
                <Link to="/install">📲 Télécharger l'app</Link>
              </Button>
            </div>

            <div className="mt-10 flex flex-wrap gap-x-10 gap-y-3 text-sm text-zinc-400 font-mono">
              <span>● 4K Dolby Atmos</span>
              <span>● 0€ pour les artistes</span>
              <span>● IA fanbase intégrée</span>
              <span>● Paiement instantané</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="md:col-span-4 hidden md:block"
          >
            <div className="relative aspect-[3/4] rounded-2xl overflow-hidden border border-white/10 glow-primary">
              <img
                src="https://images.pexels.com/photos/10168223/pexels-photo-10168223.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Artist spotlight"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-xs font-mono uppercase tracking-widest">EN DIRECT</span>
                </div>
                <p className="font-display font-black text-xl">Nova Lumière</p>
                <p className="text-sm text-zinc-400">1.2M spectateurs</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <section className="border-y border-white/5 py-6 overflow-hidden bg-[#0a0a0a]">
        <div className="marquee-track">
          {[...artistNames, ...artistNames].map((n, i) => (
            <span key={`${n}-${i}`} className="font-display font-black text-3xl text-zinc-700 mx-10 whitespace-nowrap">
              {n} <span className="text-[#E11D48]">★</span>
            </span>
          ))}
        </div>
      </section>

      {/* Features bento */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="mb-14 max-w-2xl">
          <p className="text-xs uppercase tracking-[0.3em] text-[#E11D48] font-bold mb-3">Pourquoi XooKmédia</p>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight">
            Tout pour transformer une scène <br/>en empire.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.07 }}
              className="p-7 rounded-2xl border border-white/5 bg-[#0f0f10] hover:border-[#E11D48]/40 hover:-translate-y-1 transition-all duration-300"
              data-testid={`feature-${i}`}
            >
              <f.icon size={28} weight="duotone" className="text-[#E11D48] mb-5" />
              <h3 className="font-display font-bold text-xl mb-2">{f.title}</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section (no public financial terms) */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#F59E0B] font-bold mb-3">Studio mobile, scène mondiale</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-6">
              De votre salon<br/>aux millions de fans.
            </h2>
            <p className="text-zinc-300 text-lg leading-relaxed">
              Un compte, un live, une fanbase qui grandit. XooKmédia vous offre le studio,
              la billetterie, le chat live, l'IA assistante et l'identité visuelle —
              tout pour transformer chaque concert en moment culte.
            </p>
            <div className="mt-8 flex gap-4">
              <Button data-testid="cta-bottom-signup" className="bg-[#E11D48] hover:bg-[#BE123C] text-white" asChild>
                <Link to="/signup?role=artist">Créer mon compte artiste</Link>
              </Button>
            </div>
          </div>

          <div className="relative aspect-square rounded-3xl border border-white/10 overflow-hidden">
            <img src="https://images.pexels.com/photos/8512432/pexels-photo-8512432.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#E11D48]/40 via-transparent to-[#F59E0B]/20" />
            <div className="relative h-full p-10 flex flex-col justify-end">
              <div className="font-mono text-zinc-400 text-sm mb-2">SCÈNE VIRTUELLE</div>
              <div className="font-display font-black text-5xl mb-1">Sans limite</div>
              <div className="text-zinc-300 mt-4 text-sm leading-relaxed">
                Diffusez en Dolby HD vers une audience mondiale,
                interagissez en direct, et bâtissez votre légende.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 mt-10">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row justify-between gap-4 text-sm text-zinc-500">
          <div className="flex items-center gap-2">
            <Waveform weight="fill" size={18} className="text-[#E11D48]" />
            <span className="font-display font-bold text-white">XooKmédia</span>
            <span>© 2026 — Révolutionner la scène avec l'humain.</span>
          </div>
          <div className="flex gap-6">
            <Link to="/concerts" className="hover:text-white">Concerts</Link>
            <Link to="/studio" className="hover:text-white">Studio IA</Link>
            <Link to="/install" className="hover:text-white">Télécharger</Link>
            <Link to="/support" className="hover:text-white">Aide</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
